import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Star, Trash2, User } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";

const SENDER_STYLE = {
  customer: "border-neutral-200 bg-white",
  business: "border-neutral-900 bg-neutral-900 text-white",
};

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [emailEdit, setEmailEdit] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const { user } = useAuth();

  const changeEmail = async () => {
    try {
      const { data } = await api.put(`/business/customers/${encodeURIComponent(selected)}/email`, { new_email: newEmail });
      toast.success(`Email updated to ${data.email}. Old login links no longer work.`);
      setSelected(data.email);
      setEmailEdit(null);
      setNewEmail("");
      api.get("/business/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get(`/business/gdpr/${encodeURIComponent(selected)}/export`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr-export-${selected}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteData = async () => {
    if (!window.confirm(`Permanently delete ALL data for ${selected}? Submissions, conversations, messages, reviews, drafts and login links will be removed. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/business/gdpr/${encodeURIComponent(selected)}`);
      toast.success(`Deleted: ${Object.entries(data.deleted).map(([k, v]) => `${v} ${k}`).join(", ")}`);
      setSelected(null);
      setDetail(null);
      api.get("/business/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    api.get("/business/customers").then(({ data }) => setCustomers(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/business/customers/${encodeURIComponent(selected)}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => toast.error(apiError(e)));
  }, [selected]);

  return (
    <div data-testid="customers-page">
      <h1 className="text-3xl font-black">Customers</h1>
      <p className="text-sm text-neutral-500 mt-1">A full timeline of every form, message, file and rating for each customer.</p>

      <div className="mt-8 grid lg:grid-cols-[340px_1fr] gap-4 items-start">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="customers-list">
          {!customers ? (
            <p className="p-6 text-sm text-neutral-400">Loading…</p>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center" data-testid="customers-empty">
              <User className="h-10 w-10 text-neutral-200 mx-auto" />
              <p className="text-sm text-neutral-400 mt-3">No customers yet.</p>
            </div>
          ) : (
            customers.map((c) => (
              <button
                key={c.email}
                onClick={() => setSelected(c.email)}
                data-testid={`customer-item-${c.email}`}
                className={`w-full text-left p-4 border-b border-[#E5E5E5] transition-colors duration-150 ${selected === c.email ? "bg-neutral-50" : "hover:bg-neutral-50"}`}
              >
                <div className="font-medium text-sm truncate">{c.name || c.email}</div>
                <div className="text-xs text-neutral-400 mt-0.5 truncate">{c.email}</div>
                <div className="text-xs text-neutral-400 mt-1.5">
                  {c.conversations} conversation(s) · {c.submissions} submission(s)
                </div>
              </button>
            ))
          )}
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6 min-h-[300px]" data-testid="customer-detail">
          {!selected ? (
            <p className="text-sm text-neutral-400 text-center py-16">Select a customer to see their full timeline.</p>
          ) : !detail ? (
            <p className="text-sm text-neutral-400">Loading…</p>
          ) : (
            <>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-black" data-testid="customer-name">{detail.name || detail.email}</h2>
                  <p className="text-sm text-neutral-500">{detail.email}</p>
                </div>
                {detail.reviews.length > 0 && (
                  <div className="flex items-center gap-1" data-testid="customer-rating">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${i <= Math.round(detail.reviews.reduce((a, r) => a + r.score, 0) / detail.reviews.length) ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`}
                      />
                    ))}
                    <span className="text-xs text-neutral-400 ml-1">({detail.reviews.length})</span>
                  </div>
                )}
              </div>
              <div className="text-xs text-neutral-400 mt-2">
                {detail.conversations.length} conversation(s) · {detail.submissions.length} submission(s)
              </div>
              <div className="mt-4" data-testid="email-change-section">
                {!emailEdit ? (
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setEmailEdit(selected); setNewEmail(""); }} data-testid="change-email-button">
                    Change email
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="new-email@example.com"
                      className="h-9 w-64 rounded-lg border border-[#E5E5E5] px-3 text-sm"
                      data-testid="new-email-input"
                    />
                    <Button size="sm" className="rounded-full" onClick={changeEmail} disabled={!newEmail.includes("@")} data-testid="save-email-button">Save</Button>
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setEmailEdit(null)} data-testid="cancel-email-button">Cancel</Button>
                  </div>
                )}
                <p className="text-xs text-neutral-400 mt-1.5">Use when the customer loses access to their inbox — moves all their data and disables old login links.</p>
              </div>
              {user?.staff_role !== "agent" && (
                <div className="mt-4 flex gap-2" data-testid="gdpr-actions">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={exportData} data-testid="gdpr-export-button">
                    <Download className="h-3.5 w-3.5 mr-1" /> Export data
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={deleteData} data-testid="gdpr-delete-button">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete data
                  </Button>
                </div>
              )}

              <h3 className="mt-8 text-sm font-bold uppercase tracking-widest text-neutral-400">Timeline</h3>
              <div className="mt-4 space-y-3" data-testid="customer-timeline">
                {detail.timeline.map((m) => (
                  <div key={m.id} className={`border rounded-xl px-4 py-3 ${SENDER_STYLE[m.sender] || ""}`} data-testid={`timeline-${m.id}`}>
                    <div className={`text-[10px] uppercase tracking-widest mb-1 flex items-center gap-2 flex-wrap ${m.sender === "business" ? "text-neutral-400" : "text-neutral-500"}`}>
                      <span>{m.sender === "business" ? "Business" : "Customer"}</span>
                      {m.internal && <span className="text-amber-600">staff only</span>}
                      {m.kind === "file_request" && <span>file request</span>}
                      {m.via && <span>via {m.via}</span>}
                      {m.broadcast && <span>broadcast</span>}
                      <span>· {m.form_title}</span>
                      <span>· {new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                    {m.attachments?.length > 0 && (
                      <div className={`text-xs mt-1.5 ${m.sender === "business" ? "text-neutral-300" : "text-neutral-500"}`}>
                        {m.attachments.length} file(s): {m.attachments.map((a) => a.original).join(", ")}
                      </div>
                    )}
                  </div>
                ))}
                {detail.timeline.length === 0 && <p className="text-sm text-neutral-400">No activity yet.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
